import { ShieldAlert, Building2, Loader2, RotateCw, ExternalLink } from "lucide-react";
import { ENV, ENV_PROBLEM_TEXT } from "@/lib/environment";
import { teamManagementEntry } from "@/lib/appLinks";

const RUNTIME = ["core_identity", "core_env_mismatch", "stack_identity", "stack_schema", "stack_mismatch"];
const problemText = (key) => (ENV_PROBLEM_TEXT[key] || key)
  .replace("{label}", ENV.label).replace("{expectedHost}", ENV.expectedHost || "–");

export function EnvBlocker({ problems, title = "Anmeldung gesperrt", note = "Es werden keine Anmeldedaten gesendet." }) {
  return (
    <div role="alert" data-testid="env-blocker"
      className="rounded-[12px] border border-v3-line bg-v3-sand p-[20px] text-v3-ink">
      <h2 className="flex items-center gap-2 text-[17px] font-bold">
        <ShieldAlert className="h-5 w-5 shrink-0 text-v3-amber" aria-hidden="true" /> {title}
      </h2>
      <p className="mt-2 text-[14px] text-v3-ink">
        {problems.some((p) => RUNTIME.includes(p)) ? "Der angesprochene Core passt nicht zu dieser Umgebung." : "Die Konfiguration dieser App passt nicht zur Umgebung."} {note}
      </p>
      <ul className="mt-3 list-disc space-y-1 pl-5 text-[14px]" data-testid="env-blocker-problems">
        {problems.map((p) => <li key={p} data-testid={`env-problem-${p}`}>{problemText(p)}</li>)}
      </ul>
      <dl className="mt-4 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 break-all text-[12.5px] text-v3-muted" data-testid="env-blocker-details">
        <dt className="font-bold">Umgebung</dt><dd>{ENV.label}</dd>
        <dt className="font-bold">App-Adresse</dt><dd>{ENV.appHost || "–"}</dd>
        <dt className="font-bold">Core</dt><dd>{ENV.core || "–"}</dd>
      </dl>
    </div>
  );
}

export function CoreGateNotice({ gate, what = "Die Anmeldung" }) {
  if (gate.status === "checking") {
    return (
      <p role="status" data-testid="core-gate-checking" className="mt-[14px] flex items-center gap-2 text-[14px] text-v3-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Verbindung zum Core wird geprüft …
      </p>
    );
  }
  if (gate.status !== "unreachable") return null;
  return (
    <div role="alert" data-testid="core-gate-unreachable" className="mt-[14px] rounded-[12px] bg-v3-sand px-[16px] py-[12px] text-[14px] text-v3-ink">
      <p>Der Core ist gerade nicht erreichbar. {what} ist erst nach erfolgreicher Prüfung möglich.</p>
      <button type="button" onClick={gate.retry} data-testid="core-gate-retry"
        className="mt-2 inline-flex min-h-[40px] items-center gap-1.5 rounded-[8px] border border-v3-line bg-white px-3 text-[13px] font-bold text-v3-ink hover:bg-v3-lilac">
        <RotateCw className="h-3.5 w-3.5" aria-hidden="true" /> Erneut prüfen
      </button>
    </div>
  );
}

// Owner/team navigation stays in the explicitly assigned environment.
export function InternalAccessNote({ gate }) {
  const entry = teamManagementEntry(ENV, gate?.status === "ok");
  return (
    <section aria-labelledby="internal-access-title" data-testid="internal-access-note"
      className="mt-[28px] rounded-[12px] border border-v3-line bg-white p-[18px] !text-lg text-v3-muted">
      <h2 id="internal-access-title" className="flex items-center gap-2 !text-lg font-bold text-v3-ink">
        <Building2 className="h-5 w-5 shrink-0 text-v3-magenta" aria-hidden="true" /> Für Team und Inhaber
      </h2>
      <p className="mt-2">Mit deinem Team- oder Inhaberkonto verwaltest du VISIBILIS im Control OS. Persönliche Mitgliederkonten bleiben davon getrennt.</p>
      {entry ? (
        <a href={entry.href} rel="noopener noreferrer" referrerPolicy="no-referrer" data-testid="internal-access-link"
          className="mt-3 inline-flex min-h-[48px] flex-wrap items-center gap-2 rounded !text-lg font-bold text-v3-magenta underline underline-offset-4">
          Zentrale Verwaltung öffnen ({entry.label}) <ExternalLink className="h-5 w-5" aria-hidden="true" />
        </a>
      ) : (
        <p role="status" data-testid="internal-access-unavailable" className="mt-3 font-bold">Für diese Umgebung ist noch kein geprüftes Verwaltungsziel verfügbar.</p>
      )}
      <p className="mt-2 !text-base">Im Control OS gelten dein persönliches Teamkonto und die Zwei-Faktor-Anmeldung.</p>
    </section>
  );
}
